import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Target, Building2, Users, CheckSquare } from "lucide-react";
import api from "@/lib/api";

export function CommandPalette({ open, setOpen }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ leads: [], companies: [], contacts: [], tasks: [] });
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [setOpen]);

  useEffect(() => {
    if (!query || query.length < 1) { setResults({ leads: [], companies: [], contacts: [], tasks: [] }); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(query)}`); setResults(data); } catch (e) { /* */ }
    }, 180);
    return () => clearTimeout(t);
  }, [query]);

  const go = useCallback((path) => { setOpen(false); setQuery(""); navigate(path); }, [navigate, setOpen]);

  const total = results.leads.length + results.companies.length + results.contacts.length + results.tasks.length;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput data-testid="command-palette-input" placeholder="Search leads, companies, contacts, tasks…" value={query} onValueChange={setQuery} />
      <CommandList>
        {query && total === 0 && <CommandEmpty>No results found.</CommandEmpty>}
        {!query && <div className="px-4 py-6 text-center text-sm text-slate-400">Type to search across your CRM…</div>}
        {results.leads.length > 0 && (
          <CommandGroup heading="Leads">
            {results.leads.map((r) => (
              <CommandItem key={r.id} value={`lead-${r.id}-${r.title}`} onSelect={() => go(`/leads/${r.id}`)} className="gap-2">
                <Target className="h-4 w-4 text-[#0057A8]" />
                <span className="flex-1">{r.title}</span>
                <span className="text-xs text-slate-400">{r.subtitle}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.companies.length > 0 && (
          <CommandGroup heading="Companies">
            {results.companies.map((r) => (
              <CommandItem key={r.id} value={`company-${r.id}-${r.title}`} onSelect={() => go(`/companies`)} className="gap-2">
                <Building2 className="h-4 w-4 text-[#0057A8]" />
                <span className="flex-1">{r.title}</span>
                <span className="text-xs text-slate-400">{r.subtitle}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.contacts.length > 0 && (
          <CommandGroup heading="Contacts">
            {results.contacts.map((r) => (
              <CommandItem key={r.id} value={`contact-${r.id}-${r.title}`} onSelect={() => go(`/contacts`)} className="gap-2">
                <Users className="h-4 w-4 text-[#0057A8]" />
                <span className="flex-1">{r.title}</span>
                <span className="text-xs text-slate-400">{r.subtitle}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.tasks.length > 0 && (
          <CommandGroup heading="Tasks">
            {results.tasks.map((r) => (
              <CommandItem key={r.id} value={`task-${r.id}-${r.title}`} onSelect={() => go(r.lead_id ? `/leads/${r.lead_id}` : "/tasks")} className="gap-2">
                <CheckSquare className="h-4 w-4 text-[#0057A8]" />
                <span className="flex-1">{r.title}</span>
                <span className="text-xs text-slate-400">{r.subtitle}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
